import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { useGetLiveAiCallsQuery } from "@/store/rest_api";
import LiveCallCard from "./LiveCallCard";

/** Shown collapsed beyond this many, so a busy campaign doesn't bury the rest of the page. */
const VISIBLE_LIMIT = 4;

/**
 * AI calls happening right now, so someone opening the AI agents area sees live conversations
 * rather than discovering them in the call history afterwards.
 *
 * Built for many concurrent calls: one agent can run several at once (campaigns default to
 * `callsPerAgent: 3`, and inbound is unbounded). Only one call can be expanded at a time — each
 * open card holds two websocket connections (transcript + sentiment), so letting every card open
 * at once would multiply connections by the number of live calls.
 *
 * Renders nothing when no calls are live — it sits above normal page content and shouldn't take up
 * space or draw the eye when it has nothing to report.
 */
export default function LiveCallsSection({ agentId }: { agentId?: string }) {
  const { data: liveCalls } = useGetLiveAiCallsQuery(undefined, {
    pollingInterval: 5000,
    refetchOnMountOrArgChange: true,
  });

  const [expandedCallId, setExpandedCallId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  // On an agent's own page, only that agent's calls are relevant.
  const calls = useMemo(
    () => (liveCalls ?? []).filter((call) => !agentId || call.agentId === agentId),
    [liveCalls, agentId],
  );

  // How many agents are busy — the interesting number when one agent runs several calls at once.
  const agentCount = useMemo(
    () => new Set(calls.map((call) => call.agentId)).size,
    [calls],
  );

  if (calls.length === 0) return null;

  const visible = showAll ? calls : calls.slice(0, VISIBLE_LIMIT);
  const hidden = calls.length - visible.length;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-sm font-semibold">
          {calls.length === 1 ? "Call in progress" : `${calls.length} calls in progress`}
        </h2>
        {!agentId && agentCount > 0 && calls.length > agentCount && (
          <span className="text-xs text-muted-foreground">
            across {agentCount} {agentCount === 1 ? "agent" : "agents"}
          </span>
        )}
        <span className="text-xs text-muted-foreground">Live now</span>
      </div>

      <div className="space-y-3">
        {visible.map((call) => (
          <LiveCallCard
            key={call.callId}
            call={call}
            expanded={expandedCallId === call.callId}
            onToggle={() =>
              setExpandedCallId((current) => (current === call.callId ? null : call.callId))
            }
          />
        ))}
      </div>

      {hidden > 0 && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll(true)}>
          Show {hidden} more {hidden === 1 ? "call" : "calls"}
        </Button>
      )}
      {showAll && calls.length > VISIBLE_LIMIT && (
        <Button variant="ghost" size="sm" onClick={() => setShowAll(false)}>
          Show fewer
        </Button>
      )}
    </div>
  );
}
